import { useTranslations } from '@vocab/react';
import {
  Dialog,
  RadioGroup,
  RadioItem,
  Stack,
  TextLink,
  Text,
  Button,
  Inline,
} from 'braid-design-system';
import { useState, type FormEvent } from 'react';

import errorTranslations from 'src/core/domain/errors/.vocab';
import { ReportError } from 'src/core/domain/errors/reportErrors';
import { useCommunityGuidelines } from 'src/core/utils/communityGuidelinesUils';
import type { InteractionType } from 'src/provider/optionDialog';
import {
  reportComment,
  reportReply,
  reportThread,
} from 'src/services/threadInteractionService';

import translations from './.vocab';

import './ReportDialog.css';

interface ReportDialogProps {
  id: string;
  contentId: string;
  interaction: InteractionType;
  open: boolean;
  onClose: () => void;
  onReportSuccess: (
    linkAction: string,
    linkPosition: string,
    reportReason: string,
    message: string,
  ) => void;
  onReportError: (message: string) => void;
}

export default ({
  id,
  contentId,
  interaction,
  open,
  onClose,
  onReportSuccess,
  onReportError,
}: ReportDialogProps) => {
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);

  const [reportReason, setReportReason] = useState('');

  const handleReportReasonChange = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => setReportReason(value);

  const handleDialogClose = () => {
    onClose();
    setReportReason('');
  };

  const handleReportError = (err: unknown) => {
    const reportError = err instanceof ReportError ? err : new ReportError();
    const message = errorTranslation(reportError.key);
    onReportError(message);
  };

  const handleThreadReport = async () => {
    try {
      await reportThread({ id: contentId, reason: reportReason });
      onReportSuccess(
        'report thread',
        'thread',
        reportReason,
        'Thread reported successfully',
      );
    } catch (err) {
      handleReportError(err);
    }
  };

  const handleCommentReport = async () => {
    try {
      await reportComment({ id: contentId, reason: reportReason });
      onReportSuccess(
        'report comment',
        'thread comment',
        reportReason,
        'Thread reported successfully',
      );
    } catch (err) {
      handleReportError(err);
    }
  };

  const handleReplyReport = async () => {
    try {
      await reportReply({ id: contentId, reason: reportReason });
      onReportSuccess(
        'report thread reply',
        'thread reply',
        reportReason,
        'Thread reported successfully',
      );
    } catch (err) {
      handleReportError(err);
    }
  };

  const handleReportSubmit = async () => {
    if (!reportReason || reportReason === '') {
      handleReportError(new ReportError('NO_SELECTION'));
      handleDialogClose();
      return;
    }

    switch (interaction) {
      case 'thread':
        await handleThreadReport();
        break;
      case 'comment':
        await handleCommentReport();
        break;
      case 'reply':
        await handleReplyReport();
        break;
      default:
        break;
    }
    handleDialogClose();
  };
  const communityGuidelines = useCommunityGuidelines();
  return (
    <Dialog
      id={id}
      title={
        interaction === 'thread'
          ? t('reportThreadTitle')
          : t('reportCommentTitle')
      }
      open={open}
      onClose={handleDialogClose}
      width="small"
    >
      <Text id="fieldLabel" tone="secondary">
        {t('dialogDescription')}
      </Text>
      <RadioGroup
        id="radio-dialog"
        aria-labelledby="fieldLabel"
        value={reportReason}
        onChange={handleReportReasonChange}
      >
        <RadioItem label={t('offensive')} value={t('offensive')} />
        <RadioItem
          label={t('illegalOrFraudulent')}
          value={t('illegalOrFraudulent')}
        />
        <RadioItem label={t('sexualOrViolent')} value={t('sexualOrViolent')} />
        <RadioItem
          label={t('racialCommentary')}
          value={t('racialCommentary')}
        />
        <RadioItem
          label={t('drugRelatedReferences')}
          value={t('drugRelatedReferences')}
        />
        <RadioItem label={t('spam')} value={t('spam')} />
        <RadioItem
          label={t('commercialInNature')}
          value={t('commercialInNature')}
        />
      </RadioGroup>
      <Stack space="small">
        <Text tone="secondary">
          {t('notSureAboutReport')}
          {t('checkoutOur')}
        </Text>
        <Text tone="formAccent">
          <TextLink
            href={communityGuidelines}
            target="_blank"
            weight="weak"
            hitArea="large"
          >
            {t('communityGuidelines')}
          </TextLink>
        </Text>
      </Stack>
      <Inline space="small" align="left" collapseBelow="tablet">
        <Button variant="ghost" tone="formAccent" onClick={handleReportSubmit}>
          {t('report')}
        </Button>
        <Button
          variant="transparent"
          tone="formAccent"
          onClick={handleDialogClose}
        >
          {t('cancel')}
        </Button>
      </Inline>
    </Dialog>
  );
};
